import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';
import { Observable, Subject, debounceTime, takeUntil, first } from 'rxjs';

import { TOAST_ALERTS_TOKEN } from '@dataclouder/ngx-core';

import { WebSpeechRecognizerService } from './web-speech-recognizer.service';
import { AudioService } from './audio.service';
import { MicStateService, MicStatus } from '../components/mic/mic-state.service';

export interface SpeechRecognitionResult {
  text?: string;
  isFinal: boolean;
  error?: any;
}

@Injectable({
  providedIn: 'root',
})
export class SpeechRecognizerService {
  private toastService = inject(TOAST_ALERTS_TOKEN);
  private audioService = inject(AudioService);
  private webSpeechRecognizerService = inject(WebSpeechRecognizerService);
  private micStateService = inject(MicStateService);

  private destroy$ = new Subject<void>();

  constructor() {
    // Ensure SpeechRecognition listeners are cleaned up when the service is destroyed.
    // This is a general good practice, though destroy$ is used for specific subscriptions.
  }

  private createSpeechObservable(): Observable<string> {
    return new Observable((observer) => {
      SpeechRecognition.addListener('partialResults', (data: any) => {
        console.log('partialResults was fired', data.matches[0]);
        observer.next(data.matches[0]);
      });
      // It's important to return a teardown logic to remove the listener
      return () => {
        SpeechRecognition.removeAllListeners();
      };
    });
  }

  public startCapacitorSpeechRecognizer(langToRecognize: string): Observable<SpeechRecognitionResult> {
    return new Observable((observer) => {
      let errorTimer: any = null;
      const componentDestroyed$ = new Subject<void>();

      const init = async () => {
        try {
          const request = await SpeechRecognition.requestPermissions();
          if (request.speechRecognition !== 'granted') {
            alert('No permissions for microphone');
            observer.error({ error: 'Permission not granted' });
            observer.complete();
            return;
          }

          this.audioService.playWriting();
          this.micStateService.changeMicStatus(MicStatus.Listening);

          await SpeechRecognition.start({
            language: langToRecognize || 'es-MX', // Use provided lang or default
            maxResults: 1,
            partialResults: true,
            popup: false,
          });

          console.log('OK inicio startListening');
          if (errorTimer) clearTimeout(errorTimer); // Clear previous timer if any
          errorTimer = null; // Reset timer

          errorTimer = setTimeout(async () => {
            const listen = await SpeechRecognition.isListening();
            console.log('Hay error?', listen);
            if (!listen.listening) {
              console.log('posible errorr no longer listening', listen);
              this.audioService.playFinishRecordingAudio2();
              this.micStateService.changeMicStatus(MicStatus.Inactive);
              observer.next({ isFinal: true, error: 'Not listening after timeout' });
              observer.complete();
              componentDestroyed$.next();
              componentDestroyed$.complete();
            }
          }, 1100);

          const speech$ = this.createSpeechObservable();

          speech$.pipe(takeUntil(componentDestroyed$)).subscribe((text) => {
            if (errorTimer) {
              clearTimeout(errorTimer);
              errorTimer = null;
            }
            observer.next({ text, isFinal: false });
          });

          speech$.pipe(takeUntil(componentDestroyed$), debounceTime(1500)).subscribe({
            next: (finalText) => {
              console.log('Speech finished - silence detected', finalText);
              this.micStateService.changeMicStatus(MicStatus.Inactive);
              SpeechRecognition.stop();
              SpeechRecognition.removeAllListeners(); // Clean up all listeners for SR
              this.audioService.playFinishRecordingAudio();
              observer.next({ text: finalText, isFinal: true });
              observer.complete();
              componentDestroyed$.next();
              componentDestroyed$.complete();
            },
            error: (err) => {
              console.error('Error in debounced speech subscription', err);
              this.micStateService.changeMicStatus(MicStatus.Inactive);
              SpeechRecognition.stop();
              SpeechRecognition.removeAllListeners();
              observer.error({ error: err });
              observer.complete();
              componentDestroyed$.next();
              componentDestroyed$.complete();
            },
          });
        } catch (error) {
          console.error('Error starting Capacitor Speech Recognizer', error);
          this.micStateService.changeMicStatus(MicStatus.Inactive);
          observer.error({ error });
          observer.complete();
          componentDestroyed$.next();
          componentDestroyed$.complete();
        }
      };

      init();

      // Teardown logic for the Observable
      return () => {
        console.log('Capacitor Speech Recognizer Observable teardown');
        if (errorTimer) {
          clearTimeout(errorTimer);
        }
        SpeechRecognition.stop().catch((err) => console.warn('Error stopping SR on teardown', err));
        SpeechRecognition.removeAllListeners();
        this.micStateService.changeMicStatus(MicStatus.Inactive); // Ensure state is reset
        componentDestroyed$.next();
        componentDestroyed$.complete();
      };
    });
  }

  public startWebSpeechRecognizer(langToRecognize: string): Observable<SpeechRecognitionResult> {
    return new Observable((observer) => {
      console.log('starting web speech recognizer with language', langToRecognize);

      this.audioService.playWriting();
      this.webSpeechRecognizerService.initialize(langToRecognize);
      this.micStateService.changeMicStatus(MicStatus.Listening);

      const onResultSub = this.webSpeechRecognizerService.onResult().subscribe((data: any) => {
        observer.next({ text: data.content, isFinal: data.isFinal });
      });

      const onEndSub = this.webSpeechRecognizerService
        .onEnd()
        .pipe(first()) // Ensure this completes after the first emission
        .subscribe((data: any) => {
          this.micStateService.changeMicStatus(MicStatus.Inactive);
          observer.next({ text: data.content, isFinal: true });
          observer.complete(); // Complete the main observable
        });

      const onErrorSub = this.webSpeechRecognizerService.onError().subscribe((data: any) => {
        console.log('error -- --', data.error);
        const subtitle = typeof data.content === 'string' ? data.content : typeof data.error === 'string' ? data.error : 'Unknown error';
        this.toastService.error({ title: 'No puede grabar tu voz', subtitle });
        this.micStateService.changeMicStatus(MicStatus.Inactive);
        observer.error({ error: data.error || subtitle });
        observer.complete(); // Complete on error as well
      });

      this.webSpeechRecognizerService.start();

      // Teardown logic for this observable
      return () => {
        console.log('Web Speech Recognizer Observable teardown');
        onResultSub.unsubscribe();
        onEndSub.unsubscribe();
        onErrorSub.unsubscribe();
        this.webSpeechRecognizerService.stop(); // Ensure web speech is stopped
        this.micStateService.changeMicStatus(MicStatus.Inactive); // Reset state
      };
    });
  }

  public stopCapacitorRecognition(): void {
    SpeechRecognition.stop()
      .then(() => {
        console.log('Capacitor SpeechRecognition stopped by stopCapacitorRecognition');
        this.micStateService.changeMicStatus(MicStatus.Inactive);
        SpeechRecognition.removeAllListeners();
      })
      .catch((e) => console.error('Error stopping Capacitor SpeechRecognition', e));
  }

  public stopWebRecognition(): void {
    this.webSpeechRecognizerService.stop();
    this.micStateService.changeMicStatus(MicStatus.Inactive);
  }

  // Call this method when the component/service holding this logic is destroyed
  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Ensure all recognition processes are stopped and listeners removed
    if (Capacitor.isNativePlatform()) {
      SpeechRecognition.stop().catch((e) => console.warn('Error stopping SR on ngOnDestroy', e));
      SpeechRecognition.removeAllListeners();
    } else {
      this.webSpeechRecognizerService.stop();
    }
    this.micStateService.changeMicStatus(MicStatus.Inactive);
  }
}
